import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import NextHead from 'next/head';

export default function Privacy() {
  const router = useRouter();
  const { i18n } = useTranslation();

  useEffect(() => {
    const locale = i18n.language || 'ko';
    if (locale !== 'ko') {
      router.push(`/policy/privacy-${locale}`);
    }
  }, [i18n.language, router]);

  return (
    <Layout>
      <NextHead>
        <title>BGMs - 개인정보처리방침</title>
        <meta name="description" content="BGMs (브금쓰)로 YouTube 플레이리스트를 공유하고, 공간에 음악을 채워보세요." />
        <meta property="og:title" content="BGMs - 개인정보처리방침" />
        <meta name="subject" content="BGMs (브금쓰)로 YouTube 플레이리스트를 공유하고, 공간에 음악을 채워보세요." />
        <meta property="og:description" content="BGMs (브금쓰)로 YouTube 플레이리스트를 공유하고, 공간에 음악을 채워보세요." />
        <meta property="og:url" content="https://bgms.live/policy/privacy" />
        <meta
          name="twitter:image"
          content="https://bgms-bucket.s3.ap-northeast-2.amazonaws.com/channel/image/bgmsMetaImg.jpg"
        />
        <meta name="twitter:domain" content="https://bgms.live/policy/privacy" />
        <meta name="twitter:title" content="BGMs - 개인정보처리방침" />
        <meta name="twitter:description" content="BGMs (브금쓰)로 YouTube 플레이리스트를 공유하고, 공간에 음악을 채워보세요." />
      </NextHead>

      <div className="max-w-4xl mx-auto px-6 py-16 animate-fade-in">
        {/* Header */}
        <div className="mb-16">
          <div className="inline-block px-4 py-1 bg-lime/10 text-lime rounded-full text-sm font-bold mb-4">
            Privacy Policy
          </div>
          <h1 className="text-5xl font-bold mb-6 text-white">개인정보처리방침</h1>
          <div className="space-y-2 text-gray-d leading-relaxed">
            <p>주식회사 도들랩 (이하 '회사')는 개인정보보호법에 따라 이용자의 개인정보 보호 및 권익을 보호하고 개인정보와 관련한 이용자의 고충을 원활하게 처리할 수 있도록 다음과 같은 처리방침을 두고 있습니다.</p>
            <p>회사는 개인정보처리방침을 개정하는 경우 웹사이트 공지사항(또는 개별공지)을 통하여 공지할 것입니다.</p>
            <p className="text-lime font-semibold mt-4">시행일자: 2022년 11월 1일</p>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-12 text-gray-d leading-relaxed">
          {/* Section 1 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. 개인정보의 처리 목적</h2>
            <p className="mb-4 whitespace-pre-line">회사는 개인정보를 다음의 목적을 위해 처리합니다.
처리한 개인정보는 다음의 목적이외의 용도로는 사용되지 않으며 이용 목적이 변경될 시에는 사전동의를 구할 예정입니다.</p>
            <div className="space-y-4 ml-4">
              <div>
                <h3 className="text-white font-semibold mb-1">가. 홈페이지 회원가입 및 관리</h3>
                <p>회원 가입의사 확인, 회원제 서비스 제공에 따른 본인 식별·인증, 회원자격 유지·관리, 제한적 본인확인제 시행에 따른 본인확인, 서비스 부정이용 방지, 각종 고지·통지, 고충처리, 분쟁 조정을 위한 기록 보존 등을 목적으로 개인정보를 처리합니다.</p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">나. 재화 또는 서비스 제공</h3>
                <p>서비스 제공, 요금결제·정산 등을 목적으로 개인정보를 처리합니다.</p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">다. 마케팅 및 광고에의 활용</h3>
                <p>신규 서비스(제품) 개발 및 맞춤 서비스 제공, 이벤트 및 광고성 정보 제공 및 참여기회 제공, 인구통계학적 특성에 따른 서비스 제공 및 광고 게재, 서비스의 유효성 확인, 접속빈도 파악 또는 회원의 서비스 이용에 대한 통계 등을 목적으로 개인정보를 처리합니다.</p>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. 수집하는 개인 정보</h2>
            <p className="mb-4">회사는 이용계약 체결, 원활한 문의 대응, 각종 서비스 제공 등의 기본적인 서비스 제공을 위한 필수 정보와 보다 충실한 서비스의 제공을 위하여 필요한 정보만을 수집하고 있습니다.</p>
            <ul className="space-y-2 ml-4">
              <li>• 개인정보 항목: 구글 회원식별자정보</li>
              <li>• 자동 수집항목: 서비스 이용기록, 부정 이용기록, 이용 정지기록, 접속기록 및 접속일시, IP 주소, 결제 기록, 버전 정보, 단말기 정보 (모델명, OS버전, 국가 정보), 광고 ID, 오픈마켓 정보, 쿠키 (Cookie)</li>
              <li>• 수집방법: 홈페이지 및 'BGMs'가 제공하는 소프트웨어</li>
              <li>• 보유기간: 1년</li>
              <li>• 관련법령: 신용정보의 수집/처리 및 이용 등에 관한 기록: 3년, 소비자의 불만 또는 분쟁처리에 관한 기록: 3년, 대금결제 및 재화 등의 공급에 관한 기록: 5년, 계약 또는 청약철회 등에 관한 기록: 5년</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. 개인정보의 처리 및 보유 기간</h2>
            <p className="mb-4">회사는 법령에 따른 개인정보 보유·이용기간 또는 정보주체로부터 개인정보를 수집시에 동의 받은 개인정보 보유, 이용기간 내에서 개인정보를 처리, 보유합니다. 각각의 개인정보 처리 및 보유 기간은 다음과 같습니다.</p>
            <div className="ml-4">
              <p className="text-white font-semibold mb-2">보유근거</p>
              <ol className="space-y-1 list-decimal list-inside">
                <li>신용정보의 수집/처리 및 이용 등에 관한 기록: 3년</li>
                <li>소비자의 불만 또는 분쟁처리에 관한 기록: 3년</li>
                <li>대금결제 및 재화 등의 공급에 관한 기록: 5년</li>
                <li>계약 또는 청약철회 등에 관한 기록: 5년</li>
              </ol>
            </div>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. 권리 행사</h2>
            <p className="mb-4">정보주체와 법정대리인의 권리·의무 및 그 행사방법 이용자는 개인정보주체로써 다음과 같은 권리를 행사할 수 있습니다.</p>
            <div className="space-y-3 ml-4">
              <div>
                <h3 className="text-white font-semibold mb-1">가. 열람, 정정, 삭제, 처리정지 요구 권리</h3>
                <p>정보주체는 주식회사 도들랩에 대해 언제든지 개인정보 열람, 정정, 삭제, 처리정지 요구 등의 권리를 행사할 수 있습니다.</p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">나. 권리 행사 방법</h3>
                <p>제1항에 따른 권리 행사는 회사에 대해 개인정보 보호법 시행령 제41조제1항에 따라 서면, 전자우편, 모사전송(FAX) 등을 통하여 하실 수 있으며 회사는 이에 대해 지체 없이 조치하겠습니다.</p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">다. 대리인을 통한 권리 행사</h3>
                <p>제1항에 따른 권리 행사는 정보주체의 법정대리인이나 위임을 받은 자 등 대리인을 통하여 하실 수 있습니다. 이 경우 개인정보 보호법 시행규칙 별지 제11호 서식에 따른 위임장을 제출하셔야 합니다.</p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">라. 권리 제한</h3>
                <p>개인정보 열람 및 처리정지 요구는 개인정보보호법 제35조 제5항, 제37조 제2항에 의하여 정보주체의 권리가 제한될 수 있습니다.</p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">마. 삭제 요구 제한</h3>
                <p>개인정보의 정정 및 삭제 요구는 다른 법령에서 그 개인정보가 수집 대상으로 명시되어 있는 경우에는 그 삭제를 요구할 수 없습니다.</p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">바. 본인 확인</h3>
                <p>회사는 정보주체 권리에 따른 열람의 요구, 정정·삭제의 요구, 처리정지의 요구 시 열람 등 요구를 한 자가 본인이거나 정당한 대리인인지를 확인합니다.</p>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. 개인정보의 파기</h2>
            <p className="mb-4">회사는 원칙적으로 개인정보 처리목적이 달성된 경우에는 지체없이 해당 개인정보를 파기합니다. 파기의 절차, 기한 및 방법은 다음과 같습니다.</p>
            <div className="space-y-3 ml-4">
              <div>
                <h3 className="text-white font-semibold mb-1">파기절차</h3>
                <p>이용자가 입력한 정보는 목적 달성 후 별도의 DB에 옮겨져(종이의 경우 별도의 서류) 내부 방침 및 기타 관련 법령에 따라 일정기간 저장된 후 혹은 즉시 파기됩니다. 이 때, DB로 옮겨진 개인정보는 법률에 의한 경우가 아니고서는 다른 목적으로 이용되지 않습니다.</p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">파기기한</h3>
                <p>이용자의 개인정보는 개인정보의 보유기간이 경과된 경우에는 보유기간의 종료일로부터 5일 이내에, 개인정보의 처리 목적 달성, 해당 서비스의 폐지, 사업의 종료 등 그 개인정보가 불필요하게 되었을 때에는 개인정보의 처리가 불필요한 것으로 인정되는 날로부터 5일 이내에 그 개인정보를 파기합니다.</p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">파기방법</h3>
                <p>전자적 파일 형태의 정보는 기록을 재생할 수 없는 기술적 방법을 사용합니다.</p>
              </div>
            </div>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. 개인정보 자동 수집 장치의 설치 운영 및 거부에 관한 사항</h2>
            <div className="space-y-3 ml-4">
              <div>
                <h3 className="text-white font-semibold mb-1">가. 쿠키의 사용</h3>
                <p>회사는 개별적인 맞춤서비스를 제공하기 위해 이용정보를 저장하고 수시로 불러오는 '쿠키(cookie)'를 사용합니다.</p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">나. 쿠키란?</h3>
                <p>쿠키는 웹사이트를 운영하는데 이용되는 서버(http)가 이용자의 컴퓨터 브라우저에게 보내는 소량의 정보이며 이용자들의 PC 컴퓨터내의 하드디스크에 저장되기도 합니다.</p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">• 쿠키의 사용 목적</h3>
                <p>이용자가 방문한 각 서비스와 웹 사이트들에 대한 방문 및 이용형태, 인기 검색어, 보안접속 여부, 등을 파악하여 이용자에게 최적화된 정보 제공을 위해 사용됩니다.</p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">• 쿠키의 설치 · 운영 및 거부</h3>
                <p>웹 브라우저 상단의 도구 &gt; 인터넷 옵션 &gt; 개인정보 메뉴의 옵션 설정을 통해 쿠키 저장을 거부할 수 있습니다. 다만, 쿠키 저장을 거부할 경우 맞춤형 서비스 이용에 어려움이 발생할 수 있습니다.</p>
              </div>
            </div>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. 개인정보 보호책임자</h2>
            <p className="mb-4">회사는 개인정보 처리에 관한 업무를 총괄해서 책임지고, 개인정보 처리와 관련한 정보주체의 불만처리 및 피해구제 등을 위하여 아래와 같이 개인정보 보호책임자를 지정하고 있습니다.</p>
            <div className="space-y-4 ml-4">
              <div>
                <p className="text-lime font-semibold mb-2">▸ 개인정보 보호책임자</p>
                <ul className="space-y-1">
                  <li>성명: 허은호</li>
                  <li>직책: 개인정보보호책임자</li>
                  <li>전화번호: 070-7790-0709</li>
                  <li>
                    이메일:{' '}
                    <a
                      href="mailto:dev@dodlab.kr?subject=[BGMs] Inquiry email"
                      className="text-lime hover:underline">
                      dev@dodlab.kr
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <p className="mt-4">정보주체께서는 회사의 서비스를 이용하시면서 발생한 모든 개인정보 보호 관련 문의, 불만처리, 피해구제 등에 관한 사항을 개인정보 보호책임자 및 담당부서로 문의하실 수 있습니다. 회사는 정보주체의 문의에 대해 지체 없이 답변 및 처리해드릴 것입니다.</p>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. 개인정보 처리방침 변경</h2>
            <p>이 개인정보처리방침은 시행일로부터 적용되며, 법령 및 방침에 따른 변경내용의 추가, 삭제 및 정정이 있는 경우에는 변경사항의 시행 7일 전부터 공지사항을 통하여 고지할 것입니다.</p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. 개인정보의 안전성 확보 조치</h2>
            <p className="mb-4">회사는 개인정보보호법 제29조에 따라 다음과 같이 안전성 확보에 필요한 기술적/관리적 및 물리적 조치를 하고 있습니다.</p>
            <div className="space-y-3 ml-4">
              <div>
                <h3 className="text-white font-semibold mb-1">가. 정기적인 자체 감사 실시</h3>
                <p>개인정보 취급 관련 안정성 확보를 위해 정기적(분기 1회)으로 자체 감사를 실시하고 있습니다.</p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">나. 개인정보 취급 직원의 최소화 및 교육</h3>
                <p>개인정보를 취급하는 직원을 지정하고 담당자에 한정시켜 최소화하여 개인정보를 관리하는 대책을 시행하고 있습니다.</p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">다. 내부관리계획의 수립 및 시행</h3>
                <p>개인정보의 안전한 처리를 위하여 내부관리계획을 수립하고 시행하고 있습니다.</p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">라. 해킹 등에 대비한 기술적 대책</h3>
                <p>회사는 해킹이나 컴퓨터 바이러스 등에 의한 개인정보 유출 및 훼손을 막기 위하여 보안프로그램을 설치하고 주기적인 갱신·점검을 하며 외부로부터 접근이 통제된 구역에 시스템을 설치하고 기술적/물리적으로 감시 및 차단하고 있습니다.</p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">마. 개인정보의 암호화</h3>
                <p>이용자의 개인정보는 비밀번호는 암호화되어 저장 및 관리되고 있어, 본인만이 알 수 있으며 중요한 데이터는 파일 및 전송 데이터를 암호화하거나 파일 잠금 기능을 사용하는 등의 별도 보안기능을 사용하고 있습니다.</p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">바. 접속기록의 보관 및 위변조 방지</h3>
                <p>개인정보처리시스템에 접속한 기록을 최소 6개월 이상 보관, 관리하고 있으며, 접속 기록이 위변조 및 도난, 분실되지 않도록 보안기능 사용하고 있습니다.</p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">사. 개인정보에 대한 접근 제한</h3>
                <p>개인정보를 처리하는 데이터베이스시스템에 대한 접근권한의 부여, 변경, 말소를 통하여 개인정보에 대한 접근통제를 위하여 필요한 조치를 하고 있으며 침입차단시스템을 이용하여 외부로부터의 무단 접근을 통제하고 있습니다.</p>
              </div>
            </div>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10. 개인정보 열람청구</h2>
            <p className="mb-4">정보주체는 개인정보 보호법 제35조에 따른 개인정보의 열람 청구를 아래의 부서에 할 수 있습니다. 회사는 정보주체의 개인정보 열람청구가 신속하게 처리되도록 노력하겠습니다.</p>
            <div className="ml-4 mb-4">
              <p className="text-lime font-semibold mb-2">▸ 개인정보 열람청구 접수 · 처리 부서</p>
              <ul className="space-y-1">
                <li>담당자: 허은호</li>
                <li>전화번호: 070-7790-0709</li>
                <li>
                  이메일:{' '}
                  <a
                    href="mailto:dev@dodlab.kr?subject=[BGMs] Inquiry email"
                    className="text-lime hover:underline">
                    dev@dodlab.kr
                  </a>
                </li>
              </ul>
            </div>
            <p className="mb-2">정보주체께서는 제1항의 열람청구 접수․처리부서 이외에, 행정안전부의 '개인정보보호 종합지원 포털' 웹사이트(www.privacy.go.kr)를 통하여서도 개인정보 열람청구를 하실 수 있습니다.</p>
            <p>▸ 행정안전부 개인정보보호 종합지원 포털 → 개인정보 민원 → 개인정보 열람등 요구 (본인확인을 위하여 아이핀(I-PIN)이 있어야 함)</p>
          </section>
        </div>
      </div>
    </Layout>
  );
}
